/**
 * VOC 数据服务
 * 从预生成的 JSON 文件加载真实数据
 */
import type { VocRecord } from '../types/vocData';
import { requestJson } from './httpClient';

// 缓存数据
let cachedData: VocRecord[] | null = null;
let cachedJourneys: string[] | null = null;
let cachedTasks: string[] | null = null;
let cachedSubTasks: string[] | null = null;
let cachedIssueTags: string[] | null = null;
let loadingPromise: Promise<VocRecord[]> | null = null;

/**
 * 从 JSON 文件加载数据
 */
async function loadDataFromJSON(): Promise<VocRecord[]> {
  try {
    const data = await requestJson<VocRecord[]>('/voc-data.json', {
      timeoutMs: 15000,
      retries: 1,
    });
    return data;
  } catch (error) {
    console.error('加载 VOC 数据失败:', error);
    return [];
  }
}

/**
 * 获取所有数据（带缓存，异步）
 */
export async function getAllVocData(): Promise<VocRecord[]> {
  if (cachedData) {
    return cachedData;
  }

  // 防止重复加载
  if (loadingPromise) {
    return loadingPromise;
  }

  loadingPromise = loadDataFromJSON();
  cachedData = await loadingPromise;
  loadingPromise = null;

  return cachedData;
}

/**
 * 同步获取缓存的数据（如果未加载则返回空数组）
 */
export function getCachedVocData(): VocRecord[] {
  return cachedData || [];
}

/**
 * 提取唯一的枚举值
 */
function extractUniqueValues(data: VocRecord[], field: keyof VocRecord): string[] {
  const uniqueSet = new Set<string>();
  data.forEach((record) => {
    const value = record[field];
    if (value && typeof value === 'string' && value !== '未分类' && value !== 'N/A') {
      uniqueSet.add(value);
    }
  });
  return Array.from(uniqueSet).sort();
}

/**
 * 获取用户旅程列表
 */
export function getJourneys(): string[] {
  if (!cachedJourneys) {
    const data = getCachedVocData();
    cachedJourneys = extractUniqueValues(data, 'journey');
  }
  return cachedJourneys;
}

/**
 * 获取任务列表
 */
export function getTasks(): string[] {
  if (!cachedTasks) {
    const data = getCachedVocData();
    cachedTasks = extractUniqueValues(data, 'task');
  }
  return cachedTasks;
}

/**
 * 获取子任务列表
 */
export function getSubTasks(): string[] {
  if (!cachedSubTasks) {
    const data = getCachedVocData();
    cachedSubTasks = extractUniqueValues(data, 'subTask');
  }
  return cachedSubTasks;
}

/**
 * 获取问题标签列表
 */
export function getIssueTags(): string[] {
  if (!cachedIssueTags) {
    const data = getCachedVocData();
    cachedIssueTags = extractUniqueValues(data, 'issueTag');
  }
  return cachedIssueTags;
}

/**
 * 清除缓存（用于数据更新时）
 */
export function clearCache(): void {
  cachedData = null;
  cachedJourneys = null;
  cachedTasks = null;
  cachedSubTasks = null;
  cachedIssueTags = null;
}
